<template>
    <div>
      <div class="flex justify-content-between align-items-center mb-3">
          <Button icon="pi pi-chevron-left" class="p-button-text" @click="previousWeek" />
          <h3 class="m-0">Settimana del {{ formattedDate }}</h3>
          <Button icon="pi pi-chevron-right" class="p-button-text" @click="nextWeek" />
      </div>

      <div v-for="field in fields" :key="field.id" class="accordion-item">
        <div class="accordion-header" @click="toggleAccordion(field.id)">
          <h3>{{ field.name }}</h3>
        </div>
        <div v-if="isOpen(field.id)" class="accordion-content">
          <vue-cal 
            :events="field.sessions" 
            @event-click="handleSlotClick"
            :active-view="'week'"
            :disable-views="['years', 'year', 'month', 'day']"
            hide-view-selector
            hide-title-bar
            :selected-date="selectedDate"
            :time-from="timeRange.min"
            :time-to="timeRange.max"
            :time-cell-height="100"
             />
        </div>
      </div>
      <DialogComponentCampi
      :visible="isDialogCampiVisible"
      :campi="dialogCampi"
      @close="handleCloseCampi"
      @save="handleSaveCampi"
    />
    </div>
  </template>
  
  <script>
  import VueCal from 'vue-cal';
  import 'vue-cal/dist/vuecal.css';
  import EditSlotDialog from './EditSlotDialog.vue';
  import DialogComponentCampi from '../components/DialogComponentCampi.vue';
  import Service from '@/services/levante-api/levante-api';
  import { useToast } from 'primevue/usetoast';
  import { getCurrentInstance, ref, computed } from 'vue';
  import moment from 'moment';

  export default {
    name: 'AccordionFields',
    components: {
      VueCal,
      DialogComponentCampi
    },
    props: {
      fields: {
        type: Array,
        required: true,
      },
      selectedDate: {
        type: Date,
        required: true
      }
    },
    emits: ['date-change'],
    setup(props, { emit }) {
        const self = getCurrentInstance();
        self.$eventBus = self.appContext.config.globalProperties.$eventBus
        const service = new Service(self.$eventBus)
        const toast = useToast();
        const isDialogCampiVisible = ref(false);
        const dialogCampi = ref([]);
        const sessione = ref(null);
        const loading = ref(false);

        const formattedDate = computed(() => {
            return moment(props.selectedDate).format('DD/MM/YYYY');
        });

        const previousWeek = () => {
            const newDate = moment(props.selectedDate).subtract(1, 'week').toDate();
            emit('date-change', newDate);
        };

        const nextWeek = () => {
            const newDate = moment(props.selectedDate).add(1, 'week').toDate();
            emit('date-change', newDate);
        };

        const timeRange = computed(() => {
            let min = 24 * 60;
            let max = 0;
            
            if (props.fields) {
                props.fields.forEach(field => {
                    if (field.sessions) {
                        field.sessions.forEach(session => {
                            const start = moment(session.start);
                            const end = moment(session.end);
                            const startMinutes = start.hours() * 60 + start.minutes();
                            const endMinutes = end.hours() * 60 + end.minutes();
                            
                            if (startMinutes < min) min = startMinutes;
                            if (endMinutes > max) max = endMinutes;
                        });
                    }
                });
            }
            
            // If no sessions, default to 8:00 - 23:00
            if (min === 24 * 60) min = 8 * 60;
            if (max === 0) max = 23 * 60;

            // Add padding (e.g. 1 hour before and after)
            min = Math.max(0, min - 60);
            max = Math.min(24 * 60, max + 60);
            
            return { min, max };
        });

        const fetchCampi = async () => {
            try {
                const res = await service.getCampi();
                dialogCampi.value = res.items;
            } catch (error) {
                toast.add({ severity: 'error', summary: 'Errore', detail: 'Non è stato possibile ottenere i campi.' });
            }
        };

        const openDialogCampo = async (event) => {
            console.log(event);
            loading.value = true
            sessione.value = event.id
            await fetchCampi()
            .finally(()=>{
                loading.value = false
            })
            isDialogCampiVisible.value = true;
        };

        const handleCloseCampi = () => {
            isDialogCampiVisible.value = false;
        };

        const handleSaveCampi = async (updatedIscritti, forever) => {
            let fd = new FormData();
            fd.append('campo', JSON.stringify(updatedIscritti));
            fd.append('sessionId', sessione.value);
            if (forever){
                fd.append('forever', 1);
            }else{
                fd.append("forever",0);
            }
            service.saveCampoSessione(fd)
            .then((res) => {
                let severity = res.item.esito
                let summary = res.item.esito != 'success' ? 'Attenzione' : 'Successo'
                let detail = res.item.messaggio
                
                if (severity != 'warning') {
                    toast.add({severity:severity, summary: summary, detail:detail, life: 3000});                            
                }
                // Emit event to refresh data if needed or handle locally
                // For now, we might need to refresh the parent or just reload
                 window.location.reload(); 
            }).finally(()=>{
                isDialogCampiVisible.value = false;
            })
        };

        return {
            isDialogCampiVisible,
            dialogCampi,
            openDialogCampo,
            handleCloseCampi,
            handleSaveCampi,
            formattedDate,
            previousWeek,
            nextWeek,
            timeRange
        }
    },
    data() {
      return {
        openAccordions: [],
        selectedSlot: null,
      };
    },
    methods: {
      toggleAccordion(id) {
        const index = this.openAccordions.indexOf(id);
        if (index > -1) {
          this.openAccordions.splice(index, 1);
        } else {
          this.openAccordions.push(id);
        }
      },
      isOpen(id) {
        return this.openAccordions.includes(id);
      },
      handleSlotClick(event) {
        this.openDialogCampo(event);
      },
    },
  };
  </script>
  
  <style>
  .accordion-item {
    border: 1px solid #e0e0e0;
    margin: 10px 0;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  }
  
  .accordion-header {
    background-color: #f8f9fa;
    padding: 15px;
    cursor: pointer;
    transition: background-color 0.2s;
    font-weight: 600;
    color: #495057;
  }

  .accordion-header:hover {
      background-color: #e9ecef;
  }
  
  .accordion-content {
    padding: 15px;
    border-top: 1px solid #e0e0e0;
    background-color: #fff;
  }

  /* Vue Cal Customization */
  .vuecal__event {
      background-color: #e3f2fd !important;
      border-left: 4px solid #2196f3 !important;
      color: #1565c0 !important;
      border-radius: 4px;
      padding: 4px;
      font-size: 0.9em;
      font-weight: 500;
  }

  .vuecal__event-title {
      font-weight: bold;
      margin-bottom: 2px;
  }

  .vuecal__time-column .vuecal__time-cell {
      color: #6c757d;
      font-size: 0.85em;
  }

  .vuecal__header .vuecal__week-days-headings {
      border-bottom: 1px solid #dee2e6;
  }

  .vuecal__header .weekday-label {
      color: #495057;
      font-weight: 600;
  }

  .vue-cal .vue-cal-day-slot {
    display: none;
  }
  .vuecal__view-btn{
      display: none;
  }


  </style>
  